/**
 * **整单优惠的按比例分摊** —— 唯一实现（C 域 PRD 🆕9 · `DR-040` §4.2）
 *
 * ## 为什么这个文件必须独立存在
 *
 * C 域 PRD 的表里写着：「整单优惠的分摊函数**唯一实现在共享层**
 * （`packages/shared/src/nucleus/discount.ts`）—— Web / 小程序 / 服务端**共用**；
 * 🔴 **端侧不得重写分摊或取整**（只转发文案）」。
 *
 * 若端侧自己算一遍展示、服务端/另一处再算一遍落库，就会出现
 * **卡面看是摊了 3.20、库里存的是 3.19** —— 而两边都"看起来对"（都是两位小数），
 * 这种偏差只有对账时才会被发现，正是铁律 24 要拦的东西。
 *
 * ## 🔴 口径：`discount` 是**减免**，`amount` 恒为**实付净额**
 *
 * ```
 * 原价_i = amount_i + selfDiscount_i        // 派生量，不落库
 * 分摊额_i = allocate(整单优惠, 各行原价)      // 最大余数法，Σ 恒等
 * 落库 discount_i = selfDiscount_i + 分摊额_i
 * 落库 amount_i   = amount_i（**不变**）
 * ```
 *
 * ⚠️ **为什么基数里含「行内自填优惠」**：用户填的是"这一行实付多少 + 这行另减了多少"，
 * 两者合起来才是这一行的原价。整单优惠是对**原价总额**打折，所以权重取原价
 * （`DR-040` 原文「按**各子笔原价**比例分摊」）。
 *
 * ## 🔴 为什么用 `allocate()` 而不是自己写除法
 *
 * `allocate()` 已是全库的**最大余数法**定点实现（`nucleus/money.ts`）：
 * Σ 逐分恒等、平局按索引升序、可复算幂等。本项目**禁浮点除法**（规格 §22.1），
 * 任何 `total × w / Σw` 的手写形态都会在某一分上漂移。
 */

import type { Cents } from '../types/brand.js';
import { allocate, sumCents, toCents } from './money.js';

/** 参与分摊的一行：实付净额 + 该行**自己**填的优惠 */
export interface DiscountLine {
  /** 实付净额（整数分，`> 0`） */
  amount: Cents;
  /** 该行自填的优惠（整数分，`>= 0`；没有则 0） */
  selfDiscount: Cents;
}

/** 一行的**原价** = 实付 + 自填优惠（派生量，不落库） */
export function lineOriginal(line: DiscountLine): Cents {
  return toCents((line.amount as number) + (line.selfDiscount as number));
}

/** 各行原价之和 —— 整单优惠的**上界**（超过它就意味着"优惠比原价还多"，无经济意义） */
export function originalTotal(lines: readonly DiscountLine[]): Cents {
  return sumCents(lines.map(lineOriginal));
}

/**
 * 把**整单优惠**按各行原价比例分摊（不含各行自填部分）。
 *
 * 🔴 不变量：`sumCents(返回值) === total`（逐分恒等）。`total = 0` ⇒ 全 0。
 *
 * @param lines 参与分摊的行（**顺序即落库顺序** —— 平局按索引升序，改顺序会改结果，故调用方必须传稳定顺序）
 * @param total 整单优惠（整数分，`>= 0`）
 * @throws RangeError 当各行原价之和为 0（正常路径不会：`amount > 0` 已被上游挡住）
 */
export function allocateWholeDiscount(lines: readonly DiscountLine[], total: Cents): Cents[] {
  if (lines.length === 0) return [];
  if ((total as number) === 0) return lines.map(() => toCents(0));

  const weights = lines.map((l) => lineOriginal(l) as number);
  const sumW = weights.reduce((a, b) => a + b, 0);
  if (sumW <= 0) {
    throw new RangeError('整单优惠无法分摊：各行原价之和为 0');
  }
  return allocate(total, weights);
}

/**
 * **最终每行优惠** = 自填 + 分摊 —— 落库用的就是这个数组。
 *
 * 这是"两个写入者"（父级整单 + 子笔自填）唯一的合流点：
 * 负责人 2026-10-21 拍板保留子笔自填，则**必须**在这里一次性合成，
 * 否则 UI 显示与落库各算一次 ⇒ 又是同一事实两份载体。
 */
export function finalDiscounts(lines: readonly DiscountLine[], total: Cents): Cents[] {
  const shares = allocateWholeDiscount(lines, total);
  return lines.map((l, i) => toCents((l.selfDiscount as number) + (shares[i] as number)));
}
